/**
 * Sitewide one-click Add to cart for carousel / PLP cards.
 * Opens the WRAPRIG® cart drawer after a successful add.
 * Resolves variant via data-variant-id or /products/{handle}.js
 */

import { CartLinesUpdateEvent } from '@shopify/events';

const SELECTOR = '[data-atc], [data-rc-atc]';
const ADDED_LABEL = (window.Theme?.translations?.added || 'Added').toUpperCase();

/**
 * @param {HTMLElement} btn
 * @returns {Promise<string>}
 */
async function resolveVariantId(btn) {
  const existing = btn.getAttribute('data-variant-id');
  if (existing) return existing;

  const handle = btn.getAttribute('data-product-handle');
  if (!handle) throw new Error('No product');

  const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error('Product unavailable');

  const product = await response.json();
  const variants = product.variants || [];
  const variant = variants.find((item) => item.available) || variants[0];
  if (!variant?.id) throw new Error('Unavailable');

  const id = String(variant.id);
  btn.setAttribute('data-variant-id', id);
  return id;
}

/**
 * @param {string | number} variantId
 * @returns {Promise<{ sections?: Record<string, string>, cart: any }>}
 */
async function addVariantToCart(variantId) {
  const formData = new FormData();
  formData.append('id', String(variantId));
  formData.append('quantity', '1');
  formData.append('sections', 'cart-drawer-section');

  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
  const cartUrl = window.Theme?.routes?.cart_url || '/cart';

  try {
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      body: formData,
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      const message = data.description || data.message || 'Could not add to cart';
      deferred.resolve({
        detail: { didError: true, source: 'wraprig-quick-atc' },
      });
      throw new Error(message);
    }

    const cartResponse = await fetch(`${cartUrl}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    const cart = await cartResponse.json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse
        ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart)
        : cart,
      detail: {
        items: cart.items,
        sections: data.sections,
        didError: false,
        source: 'wraprig-quick-atc',
        itemCount: 1,
      },
    });

    return { sections: data.sections, cart };
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * @param {HTMLElement} btn
 */
async function handleAdd(btn) {
  if (btn.disabled || btn.classList.contains('is-loading')) return;
  if (!btn.getAttribute('data-variant-id') && !btn.getAttribute('data-product-handle')) return;

  const defaultLabel = (btn.textContent || 'ADD TO CART').trim();
  btn.classList.add('is-loading');
  btn.setAttribute('aria-busy', 'true');

  try {
    const variantId = await resolveVariantId(btn);
    await addVariantToCart(variantId);
    btn.classList.remove('is-loading');
    btn.classList.add('is-added');
    btn.textContent = ADDED_LABEL;
    // Cart drawer auto-opens via cart-drawer-component on CartLinesUpdateEvent.
    // Do not call open() again — a second open() used to close+showModal and flash.

    window.setTimeout(function () {
      btn.classList.remove('is-added');
      btn.textContent = defaultLabel;
      btn.removeAttribute('aria-busy');
    }, 1600);
  } catch (error) {
    btn.classList.remove('is-loading');
    btn.removeAttribute('aria-busy');
    const message = error?.message ? String(error.message).slice(0, 42) : defaultLabel;
    btn.textContent = message;
    window.setTimeout(function () {
      btn.textContent = defaultLabel;
    }, 2200);
  }
}

document.addEventListener(
  'click',
  function (event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const btn = target.closest(SELECTOR);
    if (!(btn instanceof HTMLElement)) return;
    if (btn.hasAttribute('disabled')) return;
    if (!btn.getAttribute('data-variant-id') && !btn.getAttribute('data-product-handle')) return;

    event.preventDefault();
    event.stopPropagation();
    handleAdd(btn);
  },
  true
);
